import {TaskEvent} from "@core/model";
import {AgendaRow} from "./row";
import {Skeleton, Stack} from "@mui/material";
import dayjs from "dayjs";
import {EmptyData} from "@components/EmptyData";
import {CalendarIcon} from "@mui/x-date-pickers";
import {translate} from "@util";

const Agenda = ({
  events,
  loading = false,
  minimize = false,
}: {
  events?: TaskEvent[];
  loading?: boolean;
  minimize?: boolean;
}) => {
  const empty = !loading && (!events || events.length === 0);

  /**
   * Splits events that span multiple days into multiple events, one for each day.
   * This is necessary because the agenda view only shows one event per day, and we want to show all events that occur on a given day,
   * even if they span multiple days.
   *
   * @param events The events to split.
   * @returns The split events.
   */
  const splitEventsIntoDays = (events: TaskEvent[]): TaskEvent[] => {
    const splitted: TaskEvent[] = [];
    events.forEach(event => {
      const eventStart = dayjs(event.start);
      const eventEnd = dayjs(event.end);
      let current = eventStart.startOf("day");
      const endMidnight = eventEnd.endOf("day");

      while (current.isBefore(endMidnight) || current.isSame(endMidnight)) {
        const isStartDay = current.isSame(eventStart, "day");
        const isEndDay = current.isSame(eventEnd, "day");

        const start = isStartDay ? eventStart.toDate() : current.toDate();
        const end = isEndDay
          ? eventEnd.toDate()
          : current.endOf("day").toDate();

        if (start < end) {
          splitted.push({...event, start, end});
        }

        current = current.add(1, "day");
      }
    });

    return splitted.sort((a, b) => a.start.getTime() - b.start.getTime());
  };

  const groupEventsByDay = (events: TaskEvent[]) => {
    const grouped: {[key: string]: TaskEvent[]} = {};
    events.forEach(event => {
      const day = dayjs(event.start).startOf("day").unix() * 1000;
      if (!grouped[day]) {
        grouped[day] = [];
      }
      grouped[day].push(event);
    });
    return grouped;
  };

  const getBorderRadius = (index: number, length: number) => {
    if (index === 0) return "8px 8px 0 0";
    if (index === length - 1) return "0 0 8px 8px";
    return 0;
  };

  const splitted = splitEventsIntoDays(events || []);
  const grouped = groupEventsByDay(splitted);

  if (loading) {
    return (
      <>
        {[...Array(3)].map((_, index) => (
          <Stack
            key={index}
            className="agenda-row"
            direction={"row"}
            bgcolor="background.paper"
            p={2}
            borderRadius={getBorderRadius(index, 3)}>
            <Skeleton variant="rectangular" width={60} height={60} />
            <Stack flex={1} minWidth={0} px={1}>
              <Skeleton variant="text" width={"40%"} />
              <Skeleton variant="text" width={"20%"} />
            </Stack>
          </Stack>
        ))}
      </>
    );
  }

  return (
    <>
      {Object.keys(grouped).map((day, index) => (
        <AgendaRow
          key={`agenda-day-${day}`}
          day={day}
          grouped={grouped}
          minimize={minimize}
          first={!minimize && index === 0}
          last={!minimize && index === Object.keys(grouped).length - 1}
        />
      ))}

      {empty && (
        <EmptyData
          sx={{p: 2}}
          message={translate("calendar.noEventsInRange")}
          Icon={CalendarIcon}
        />
      )}
    </>
  );
};

export {Agenda};
